import React, { useMemo, useState } from "react";
import Editor, { DiffEditor } from "@monaco-editor/react";
import {
  FileCode2, X, Expand, Plus, Trash2, Sparkles, GitMerge,
} from "lucide-react";
import { toast } from "sonner";
import { useOmniCraftStore } from "./store";
import TreeNode from "./TreeNode";

function buildTree(paths) {
  const root = {};
  for (const p of paths) {
    const parts = p.split("/");
    let cursor = root;
    parts.forEach((part, i) => {
      const isFile = i === parts.length - 1;
      if (isFile) {
        cursor[part] = { __file: p };
      } else {
        cursor[part] = cursor[part] || {};
        cursor = cursor[part];
      }
    });
  }
  return root;
}

function pickLanguage(path) {
  if (path.endsWith(".tsx") || path.endsWith(".ts")) return "typescript";
  if (path.endsWith(".jsx") || path.endsWith(".js")) return "javascript";
  if (path.endsWith(".json")) return "json";
  if (path.endsWith(".css")) return "css";
  if (path.endsWith(".html")) return "html";
  if (path.endsWith(".md")) return "markdown";
  return "plaintext";
}

function GeminiAssist({ onAction, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="omnicraft-gemini-assist"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] hover:bg-emerald-500/20 disabled:opacity-40"
      >
        <Sparkles size={11} /> Gemini Assist
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 bg-[#111420] border border-white/10 rounded-md shadow-2xl min-w-[220px] overflow-hidden">
          {[
            { id: "imports", label: "Optimize Imports" },
            { id: "structure", label: "Suggest Component Structure" },
            { id: "tests", label: "Generate Tests" },
          ].map((a) => (
            <button
              key={a.id}
              onClick={() => {
                setOpen(false);
                onAction(a);
              }}
              className="block w-full text-left px-3 py-2 text-xs text-white/80 hover:bg-white/5"
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CodeDrawer({ expanded, onToggleExpand }) {
  const s = useOmniCraftStore();
  const paths = Object.keys(s.files).sort();
  const tree = useMemo(() => buildTree(paths), [paths.join("|")]);
  const activeFile = s.activePath;
  const conflict = s.conflicts.find((c) => c.path === activeFile);
  const runningPrompt = useOmniCraftStore((st) => st.streaming);

  function handleChange(v) {
    if (activeFile) s.setFileContent(activeFile, v ?? "");
  }

  function resolveKeep(which) {
    const { path, user, ai } = conflict;
    const text = which === "user" ? user : ai;
    s.resolveConflict(path, text);
    toast.success(`Kept ${which === "user" ? "your" : "AI"} version`);
  }

  function createFile() {
    const name = window.prompt("New file path (e.g. src/components/Card.tsx)");
    if (!name) return;
    if (s.files[name] !== undefined) return toast.error("File already exists");
    s.setFileContent(name, "");
    s.openFile(name);
  }

  function assist(action) {
    const promptText = {
      imports: "Refactor the current file to sort/group imports and remove unused ones.",
      structure: "Suggest a better component structure for this file and produce the refactored version.",
      tests: "Generate a Vitest test file for the current file. Add it as tests/{basename}.test.ts.",
    }[action.id];
    toast.info("Sending Gemini Assist request…");
    // Fire-and-forget via chat pane's send flow: put into prompt textarea by dispatch
    const evt = new CustomEvent("omnicraft:assist", { detail: { prompt: `${promptText}\n\nActive file: ${activeFile}` } });
    window.dispatchEvent(evt);
  }

  return (
    <div className={`bg-[#0B0D12] text-white flex flex-col h-full`}>
      <div className="border-b border-white/10 px-3 py-1.5 flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 overflow-x-auto flex-1 min-w-0">
          {s.openTabs.map((path) => (
            <div
              key={path}
              onClick={() => s.openFile(path)}
              className={`flex items-center gap-1 px-2 py-1 rounded-t text-[11px] cursor-pointer flex-shrink-0 ${
                path === activeFile ? "bg-white/10 text-white" : "text-white/50 hover:bg-white/5"
              }`}
            >
              <FileCode2 size={10} />
              <span className="font-mono">{path.split("/").pop()}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  s.closeTab(path);
                }}
                className="text-white/30 hover:text-white/70"
              >
                <X size={10} />
              </button>
            </div>
          ))}
          <button
            onClick={createFile}
            className="p-1 rounded hover:bg-white/5 text-white/40 flex-shrink-0"
            data-testid="omnicraft-new-file"
            title="New file"
          >
            <Plus size={12} />
          </button>
        </div>
        <GeminiAssist onAction={assist} disabled={runningPrompt} />
        <button
          onClick={onToggleExpand}
          data-testid="omnicraft-expand-code"
          className="p-1.5 rounded hover:bg-white/5 text-white/50"
          title="Expand code editor"
        >
          <Expand size={12} />
        </button>
      </div>

      <div className="flex-1 flex min-h-0">
        {expanded && (
          <div className="w-52 border-r border-white/10 overflow-y-auto py-2 bg-[#0A0C11]">
            <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-white/40">
              Explorer
            </div>
            {Object.entries(tree)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([k, v]) => (
                <TreeNode
                  key={k}
                  name={k}
                  node={v}
                  depth={0}
                  activePath={activeFile}
                  onOpen={s.openFile}
                  onDelete={s.removeFile}
                />
              ))}
          </div>
        )}

        <div className="flex-1 min-w-0">
          {conflict ? (
            <div className="h-full flex flex-col">
              <div className="px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-xs flex items-center gap-2">
                <GitMerge size={12} />
                <span>Merge conflict — resolve to apply</span>
                <div className="flex-1" />
                <button
                  onClick={() => resolveKeep("user")}
                  className="px-2 py-0.5 rounded bg-white/10 text-white/80 hover:bg-white/20 text-[11px]"
                  data-testid="omnicraft-keep-mine"
                >
                  Keep Mine
                </button>
                <button
                  onClick={() => resolveKeep("ai")}
                  className="px-2 py-0.5 rounded bg-emerald-500 text-black hover:bg-emerald-400 text-[11px]"
                  data-testid="omnicraft-accept-ai"
                >
                  Accept AI
                </button>
              </div>
              <DiffEditor
                theme="vs-dark"
                original={conflict.user}
                modified={conflict.ai}
                language={pickLanguage(activeFile)}
                options={{ readOnly: true, renderSideBySide: true, fontSize: 12 }}
              />
            </div>
          ) : activeFile ? (
            <Editor
              theme="vs-dark"
              path={activeFile}
              value={s.files[activeFile] ?? ""}
              language={pickLanguage(activeFile)}
              onChange={handleChange}
              options={{
                fontSize: 12,
                minimap: { enabled: expanded },
                automaticLayout: true,
                scrollBeyondLastLine: false,
                fontLigatures: true,
              }}
            />
          ) : (
            <div className="w-full h-full grid place-items-center text-white/40 text-sm">
              No file selected.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
